import { Divider, Grid, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
	DefaultValues,
	FormProvider,
	SubmitHandler,
	useForm,
} from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { FormType } from '@/api/enum/formType.enum';
import {
	Dispatch,
	forwardRef,
	SetStateAction,
	useEffect,
	useImperativeHandle,
} from 'react';
import AppDatePicker from '@/common/components/form-inputs/AppDatePicker';
import dayjs from 'dayjs';
import { API_DATE_FORMAT, notFutureDate } from '@/utils/dateHelper';
import AppTextField from '@/common/components/form-inputs/AppTextField';
import AppRadioGroup from '@/common/components/form-inputs/AppRadioGroup';
import {
	CONSENT,
	EMERGENCY_CONTACT,
	SEX_LIST,
} from '@/common/constants/common';
import AppDropDownMenu from '@/common/components/form-inputs/AppDropDownMenu';

interface Props {
	type: FormType;
	details?: any;
	setType: Dispatch<SetStateAction<FormType>>;
}

const EditConsentForm = forwardRef(({ type, setType, details }: Props, ref) => {
	const { t } = useTranslation();
	const defaultValues: DefaultValues<any> = {
		mailRequest: null,
	};

	useEffect(() => {
		if (details) methods.reset(details);
	}, [details]);

	const formSchema = Yup.object().shape({
		mailRequest: Yup.string().required(t('errorMessage.pleaseSelect')),
		applicantSignatureDate: Yup.string()
			.nullable()
			.test(notFutureDate(t('errorMessage.dateCannotBeFuture'))),
		guardianSignatureDate: Yup.string()
			.nullable()
			.test(notFutureDate(t('errorMessage.dateCannotBeFuture'))),
	});
	const methods = useForm<any>({
		defaultValues,
		resolver: yupResolver(formSchema),
		mode: 'onChange',
		shouldFocusError: true,
	});

	useImperativeHandle(ref, () => ({
		getFormData: () => methods.getValues(),
		validateForm: () => {
			methods.trigger();
			methods.formState.isValid;
			return methods.trigger().then(() => methods.formState.isValid);
		},
	}));

	return (
		<>
			<FormProvider {...methods}>
				<Typography variant='h6' sx={{ fontWeight: 'bold' }} marginBottom={2}>
					{t('preAdmission.consentForm')}
				</Typography>
				<form onSubmit={methods.handleSubmit((e: any) => e.preventDefault())}>
					<Grid container spacing={3} marginTop={0} marginBottom={3}>
						<Grid size={12}>
							<Typography variant='body2' color='common.neutral40'>
								{t('preAdmission.postalPreference') + '*'}
							</Typography>
							<AppRadioGroup
								control={methods.control}
								name='mailRequest'
								row
								options={CONSENT}
							/>
						</Grid>
						<Grid size={6}>
							<AppDatePicker
								maximumDate={dayjs().startOf('day').format(API_DATE_FORMAT)}
								control={methods.control}
								name='applicantSignatureDate'
								label={t('preAdmission.applicantSignatureDate')}
							/>
						</Grid>
						<Grid size={6} />
						<Grid size={6}>
							<AppTextField
								control={methods.control}
								name='guardianName'
								label={t('preAdmission.parentGuardianName')}
							/>
						</Grid>
						<Grid size={6}>
							<AppDatePicker
								maximumDate={dayjs().startOf('day').format(API_DATE_FORMAT)}
								control={methods.control}
								name='guardianSignatureDate'
								label={t('preAdmission.parentGuardianSignatureDate')}
							/>
						</Grid>
					</Grid>

					<Divider sx={{ marginBottom: '16px' }} />
				</form>
			</FormProvider>
		</>
	);
});

EditConsentForm.displayName = 'EditConsentForm';

export default EditConsentForm;
